<script setup>
import FileTextIcon from '@iconify-vue/ph/file-text-fill';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import PlusIcon from '@iconify-vue/ph/plus';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'label',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Libellé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.label,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cmsAttributeCode',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Attribut CMS',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.cmsAttributeCode,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'maxLength',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Longueur max',
                    width: 0,
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.maxLength,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'instructions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Instructions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    props.row.original.instructions
                        ? {
                              default: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Instructions',
                                          content:
                                              props.row.original.instructions,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir les instructions',
                                                  type: 'secondary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  ),
                          }
                        : {},
                ),
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            h(ActionButton, {
                                size: 'small',
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.ai.productContentField',
                                    {
                                        tenant: tenant.id,
                                        aiProductContentField:
                                            props.row.original.id,
                                    },
                                ),
                                icon: PencilSimpleIcon,
                                square: true,
                            }),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.ai.postProductContentFields', {
        tenant: tenant.id,
    }),
    columnPinning: {
        right: ['actions'],
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ActionButton
                size="small"
                :href="
                    route('front.projectManagement.ai.productContentField', {
                        tenant: tenant.id,
                    })
                "
                :left-icon="PlusIcon"
            >
                Ajouter
            </ActionButton>
        </template>
    </DataTableLayout>
</template>
